import './style.css'
import { initLenis } from './animations/lenis'
import { initHeroAnimations } from './animations/hero'
import { initTextMask } from './animations/text-mask'
import { initQuoteLock } from './animations/quote'
import { initDrawPath } from './animations/draw-path'
import { initHearts } from './animations/hearts'
import { initMap } from './animations/map'
import { initBackgroundSwitch } from './animations/background-switch'
import { initTransition } from './animations/transition'
import { initHeart3D } from './animations/heart-3d'
import { initLetter } from './animations/letter'
import { initConstellation } from './animations/constellation'
import { initFinaleScene } from './animations/finale'
import { initTimer } from './components/timer'
import { initClock } from './components/clock'
import { playMusic } from './components/music'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

// Register GSAP plugins
gsap.registerPlugin(ScrollTrigger)

// Always start at top on reload/back-forward restore.
if ('scrollRestoration' in window.history) {
  window.history.scrollRestoration = 'manual'
}

function resetToTopOnLoad(): void {
  // Defensive reset in case a previous interaction left scroll-lock classes behind.
  document.documentElement.classList.remove('is-scroll-locked')
  document.body.classList.remove('is-scroll-locked')
  document.getElementById('constellation')?.classList.remove('is-locked')
  window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
}

function revealAndScrollToQuestionSection(): void {
  const questionSection = document.getElementById('question')
  if (!questionSection) return

  // Defensive unlock for mobile Safari cases where lock styles linger.
  document.documentElement.classList.remove('is-scroll-locked')
  document.body.classList.remove('is-scroll-locked')
  document.getElementById('constellation')?.classList.remove('is-locked')
  questionSection.classList.add('is-visible')

  const smoothScrollTo = (targetTop: number, durationMs = 1150) => {
    const startY = window.scrollY
    const delta = targetTop - startY
    if (Math.abs(delta) < 1) return

    const startTime = performance.now()
    const step = (now: number) => {
      const t = Math.min(1, (now - startTime) / durationMs)
      const eased = Math.min(1, 1.001 - Math.pow(2, -10 * t))
      window.scrollTo(0, startY + delta * eased)
      if (t < 1) {
        requestAnimationFrame(step)
      }
    }
    requestAnimationFrame(step)
  }

  const performScroll = () => {
    const targetTop = questionSection.getBoundingClientRect().top + window.scrollY
    smoothScrollTo(targetTop, 1150)

    // iPad/WebKit fallback: smooth retry first, hard snap only as last resort.
    window.setTimeout(() => {
      const nextTargetTop = questionSection.getBoundingClientRect().top + window.scrollY
      if (Math.abs(window.scrollY - nextTargetTop) > 24) {
        smoothScrollTo(nextTargetTop, 700)
      }
    }, 550)

    window.setTimeout(() => {
      const finalTargetTop = questionSection.getBoundingClientRect().top + window.scrollY
      if (Math.abs(window.scrollY - finalTargetTop) > 24) {
        window.scrollTo({ top: finalTargetTop, behavior: 'auto' })
        const scrollingEl = document.scrollingElement
        if (scrollingEl) scrollingEl.scrollTop = finalTargetTop
      }
    }, 1200)
  }

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      performScroll()
    })
  })
}

resetToTopOnLoad()

window.addEventListener('pageshow', () => {
  resetToTopOnLoad()
  requestAnimationFrame(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
  })
})

window.addEventListener('load', () => {
  resetToTopOnLoad()
})

// Hide loading screen
function hideLoadingScreen(): void {
  const loadingScreen = document.getElementById('loading-screen')
  if (loadingScreen) {
    // Instant hide - no fade to avoid pink->white->pink flash
    loadingScreen.remove()
  }
}

// Initialize everything when DOM is ready
document.addEventListener('DOMContentLoaded', async () => {
  window.scrollTo(0, 0)

  // Initialize smooth scrolling
  initLenis()

  // Initialize components
  initTimer()
  initClock()

  // Initialize 3D heart first (preload before showing)
  // This ensures shaders are compiled and ready
  await initHeart3D()

  // Initialize other animations
  initHeroAnimations()
  initTextMask()
  initQuoteLock()
  initDrawPath()
  initLetter()
  initConstellation({
    onConstellationComplete: () => {
      revealAndScrollToQuestionSection()
    },
  })
  initHearts()
  initBackgroundSwitch()
  initTransition()
  initFinaleScene()

  // Initialize map (will only work with valid Mapbox token)
  initMap()

  // Hide loading screen after 3D heart is ready
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      hideLoadingScreen()
      
      // Initialize music (will auto-play on first user interaction if autoplay is blocked)
      playMusic()

      // Hide the tap hint on first interaction
      const heartTapHint = document.getElementById('heart-tap-hint') as HTMLButtonElement | null
      if (heartTapHint) {
        const hideHint = () => {
          heartTapHint.style.display = 'none'
        }
        // Hide on any interaction
        document.addEventListener('pointerdown', hideHint, { once: true })
        document.addEventListener('touchstart', hideHint, { once: true, passive: true })
        document.addEventListener('scroll', hideHint, { once: true, passive: true })
      }
    })
  })

  // Log ready state
  console.log('Valentine website initialized ❤️')
})
